// assets/src/js/components/modal.js
let instance = null;

function createDOM() {
  const wrap = document.createElement('div');
  wrap.id = 'z-modal-root';
  wrap.className = 'fixed inset-0 z-[9999] hidden';

  wrap.innerHTML = `
    <button class="z-backdrop absolute inset-0 bg-black/40" aria-label="닫기"></button>

    <div class="z-panel pointer-events-auto absolute left-1/2 top-1/2 w-[92vw] -translate-x-1/2 -translate-y-1/2 bg-white shadow-xl outline-none overflow-hidden"
         role="dialog" aria-modal="true" aria-labelledby="z-modal-title">

      <!-- header -->
      <div class="z-head flex items-center justify-between gap-3 py-5 border-b-2 border-b-black mx-5">
        <div class="min-w-0">
          <p class="z-sub text-xs text-neutral-500 hidden"></p>
          <h3 id="z-modal-title" class="z-title text-lg font-semibold leading-snug"></h3>
          <p class="z-desc text-sm text-neutral-600 mt-1 hidden"></p>
        </div>

        <button type="button"
          class="z-close inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md hover:bg-neutral-100"
          aria-label="닫기">✕</button>
      </div>

      <!-- body -->
      <div class="z-body max-h-[80dvh] overflow-auto p-5">
        <!-- card layout (default) -->
        <div class="z-card flex items-start gap-4">
          <img class="z-thumb w-16 h-16 object-cover rounded-md hidden" alt="">
          <div class="z-card-content grow">
            <div class="z-card-slot"></div>
            <div class="z-card-actions mt-0 flex gap-2 justify-end"></div>
          </div>
        </div>

        <!-- custom slot (for forms / big layouts) -->
        <div class="z-custom hidden"></div>
      </div>

      <!-- footer (optional) -->
      <div class="z-foot p-5 border-t hidden">
        <div class="z-actions flex gap-2 justify-end"></div>
      </div>
    </div>
  `;

  document.body.appendChild(wrap);
  return wrap;
}

function focusTrap(container) {
  const FOCUSABLE = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
  ].join(',');

  function handler(e) {
    if (e.key !== 'Tab') return;

    const nodes = container.querySelectorAll(FOCUSABLE);
    if (!nodes.length) return;

    const first = nodes[0];
    const last = nodes[nodes.length - 1];

    if (e.shiftKey && document.activeElement === first) {
      last.focus();
      e.preventDefault();
    } else if (!e.shiftKey && document.activeElement === last) {
      first.focus();
      e.preventDefault();
    }
  }

  return { handler };
}

function applySize(panel, size) {
  // md: 480 / lg: 720 / xl: 960
  panel.classList.remove('max-w-[480px]', 'max-w-[720px]', 'max-w-[960px]');
  if (size === 'xl') panel.classList.add('max-w-[960px]');
  else if (size === 'lg') panel.classList.add('max-w-[720px]');
  else panel.classList.add('max-w-[480px]');
}

class ModalManager {
  constructor() {
    this.root = null;
    this.panel = null;
    this.backdrop = null;
    this.closeBtn = null;
    this.prevActive = null;
    this.trap = null;
    this.autoTimer = null;
  }

  ensureDOM() {
    if (this.root) return;

    this.root = createDOM();
    this.panel = this.root.querySelector('.z-panel');
    this.backdrop = this.root.querySelector('.z-backdrop');
    this.closeBtn = this.root.querySelector('.z-close');
    this.trap = focusTrap(this.panel);

    const close = () => this.close();

    this.backdrop.addEventListener('click', close);
    this.closeBtn.addEventListener('click', close);

    document.addEventListener('keydown', (e) => {
      if (!this.isOpen()) return;
      if (e.key === 'Escape') close();
      this.trap.handler(e);
    });
  }

  setContent({
    // header
    title = '',
    subtitle = '',
    description = '',

    // card thumb
    thumb = '',

    // card actions (old behavior)
    actions = null,

    // custom body slot
    html = '',
    body = null,

    // layout options
    mode = 'auto', // 'auto' | 'card' | 'custom'
    size = 'md', // 'md' | 'lg' | 'xl'

    // footer actions (optional)
    footerActions = null,
    showFooter = false,

    // whether to render default cart buttons when actions empty (for card mode)
    useDefaultCartActions = true,
  } = {}) {
    this.ensureDOM();

    // nodes
    const titleEl = this.root.querySelector('.z-title');
    const subEl = this.root.querySelector('.z-sub');
    const descEl = this.root.querySelector('.z-desc');

    const thumbEl = this.root.querySelector('.z-thumb');

    const cardWrap = this.root.querySelector('.z-card');
    const cardSlot = this.root.querySelector('.z-card-slot');
    const cardActionsBox = this.root.querySelector('.z-card-actions');

    const customBox = this.root.querySelector('.z-custom');

    const foot = this.root.querySelector('.z-foot');
    const footActionsBox = this.root.querySelector('.z-actions');

    // size
    applySize(this.panel, size);

    // header text
    titleEl.textContent = title || '';

    if (subtitle) {
      subEl.textContent = subtitle;
      subEl.classList.remove('hidden');
    } else {
      subEl.classList.add('hidden');
    }

    if (description) {
      descEl.textContent = description;
      descEl.classList.remove('hidden');
    } else {
      descEl.classList.add('hidden');
    }

    // decide mode
    let resolvedMode = mode;
    if (mode === 'auto') {
      resolvedMode = body instanceof Element || !!html ? 'custom' : 'card';
    }

    // reset
    cardSlot.innerHTML = '';
    cardActionsBox.innerHTML = '';
    customBox.innerHTML = '';

    // thumb (card mode)
    if (thumb && resolvedMode === 'card') {
      thumbEl.src = thumb;
      thumbEl.alt = title || '';
      thumbEl.classList.remove('hidden');
    } else {
      thumbEl.removeAttribute('src');
      thumbEl.alt = '';
      thumbEl.classList.add('hidden');
    }

    // render body
    if (resolvedMode === 'custom') {
      cardWrap.classList.add('hidden');
      customBox.classList.remove('hidden');

      if (body instanceof Element) {
        customBox.appendChild(body);
      } else if (html) {
        customBox.innerHTML = html;
      }
    } else {
      // card mode
      customBox.classList.add('hidden');
      cardWrap.classList.remove('hidden');

      // 기존 카드형에서는 title/sub/desc가 헤더에 있으니
      // 카드 slot은 비워두거나, 필요하면 여기서 추가 설명을 넣을 수 있음.
      // (원하면 opts.html을 cardSlot에 넣게 바꿀 수도 있음)

      const mkBtn = (label, onClick, classes) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.textContent = label;
        b.className = classes;
        b.addEventListener('click', onClick, { once: true });
        return b;
      };

      if (Array.isArray(actions) && actions.length) {
        actions.forEach((a) => {
          if (a.href) {
            const link = document.createElement('a');
            link.href = a.href;
            link.textContent = a.label || '확인';
            link.className =
              a.variant === 'primary'
                ? 'inline-flex items-center justify-center rounded-md bg-black px-3 py-2 text-sm text-white'
                : 'inline-flex items-center justify-center rounded-md border px-3 py-2 text-sm';
            cardActionsBox.appendChild(link);
          } else {
            const btn = mkBtn(
              a.label || '확인',
              a.onClick || (() => {}),
              a.variant === 'primary'
                ? 'btn-basic hover:btn-basic-hover'
                : 'inline-flex items-center justify-center rounded-md border px-3 py-2 text-sm'
            );
            cardActionsBox.appendChild(btn);
          }
        });
      } else if (useDefaultCartActions) {
        const cartUrl =
          (window.wc_cart_fragments_params &&
            wc_cart_fragments_params.cart_url) ||
          '/cart/';

        const view = document.createElement('a');
        view.href = cartUrl;
        view.textContent = '장바구니 보기';
        view.className =
          'inline-flex items-center justify-center rounded-md border px-3 py-2 text-sm';

        const cont = mkBtn(
          '계속 쇼핑',
          () => this.close(),
          'inline-flex items-center justify-center rounded-md bg-black px-3 py-2 text-sm text-white'
        );

        cardActionsBox.appendChild(view);
        cardActionsBox.appendChild(cont);
      }
    }

    // footer actions (optional)
    footActionsBox.innerHTML = '';
    if (showFooter && Array.isArray(footerActions) && footerActions.length) {
      foot.classList.remove('hidden');
      footerActions.forEach((a) => {
        if (a.href) {
          const link = document.createElement('a');
          link.href = a.href;
          link.textContent = a.label || '확인';
          link.className =
            a.variant === 'primary'
              ? 'inline-flex items-center justify-center rounded-md bg-black px-3 py-2 text-sm text-white'
              : 'inline-flex items-center justify-center rounded-md border px-3 py-2 text-sm';
          footActionsBox.appendChild(link);
        } else {
          const btn = document.createElement('button');
          btn.type = 'button';
          btn.textContent = a.label || '확인';
          btn.className =
            a.variant === 'primary'
              ? 'inline-flex items-center justify-center rounded-md bg-black px-3 py-2 text-sm text-white'
              : 'inline-flex items-center justify-center rounded-md border px-3 py-2 text-sm';
          btn.addEventListener('click', a.onClick || (() => {}), {
            once: true,
          });
          footActionsBox.appendChild(btn);
        }
      });
    } else {
      foot.classList.add('hidden');
    }
  }

  open(opts = {}, autoCloseMs = 0) {
    this.ensureDOM();
    clearTimeout(this.autoTimer);

    this.setContent(opts);

    this.prevActive = document.activeElement;

    this.root.classList.remove('hidden');
    this.root.dataset.open = 'true';

    this.panel.setAttribute('tabindex', '-1');
    this.panel.focus({ preventScroll: true });

    if (autoCloseMs > 0) {
      this.autoTimer = setTimeout(() => this.close(), autoCloseMs);
    }
  }

  close() {
    if (!this.root || !this.isOpen()) return;

    clearTimeout(this.autoTimer);

    delete this.root.dataset.open;
    this.root.classList.add('hidden');

    if (this.prevActive && typeof this.prevActive.focus === 'function') {
      this.prevActive.focus({ preventScroll: true });
    }
  }

  isOpen() {
    return !!(this.root && !this.root.classList.contains('hidden'));
  }

  update(opts = {}) {
    if (!this.isOpen()) return;
    this.setContent(opts);
  }

  openCart({ title = '', qty = 1, thumb = '' } = {}) {
    this.open(
      {
        mode: 'card',
        size: 'md',
        subtitle: '장바구니에 담겼습니다',
        title: title || '상품이 담겼습니다',
        description: qty ? `수량: ${qty}개` : '',
        thumb,
        // card actions: 비워두면 기본 cart actions 사용
      },
      2500
    );
  }
}

export function getModal() {
  if (!instance) instance = new ModalManager();
  return instance;
}

const modal = getModal();
if (!window.ZEEIN) window.ZEEIN = {};
window.ZEEIN.modal = modal;

export default modal;
